import React, { useEffect, useState } from 'react';
import { Modal, Descriptions, Spin } from 'antd';
import AxiosOICStat from '../../AxiosOICStat';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import "../../style/App.scss"

export const IndicatorShowExternal = ({ visible, record, onCancel }) => {
  const [indicatorData, setIndicatorData] = useState({});
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const language = localStorage.getItem('language') || 'en';
  const isRTL = language === 'ar';

  // redux slices
  const indicatorsState = useSelector(state => state.privateIndicators);
  const categoriesState = useSelector(state => state.categories);
  const unitsState = useSelector(state => state.units);

  // these mirror your previous title/definition logic
  const [indObj, setIndObj] = useState({});
  const [unitObj, setUnitObj] = useState({});
  const [definition, setDefinition] = useState("");

  useEffect(() => {
    if (!record?.ind_code) return;
    setLoading(true);

    AxiosOICStat.get(`/indicators/info-box-external/${record.ind_code}`)
      .then(({ data }) => {
        setIndicatorData(data);
        const fullInd = indicatorsState.value[data.ind_code];
        setIndObj(fullInd);

        const fullUnit = unitsState.value[ fullInd.unit_code ];
        setUnitObj(fullUnit);

        if (language === 'en') {
          setDefinition(data.ind_eng_exp);
          setTitle(
            <div>
              <div>{t('query.indicator_code')}: {data.ind_code}</div>
              <div>{fullInd?.is_used && fullInd?.optional_name ? fullInd.optional_name : fullInd?.ind_name_eng} [{fullUnit?.unit_name_eng || 'No unit'}]</div>
            </div>
          );
        }
        else if (language === 'fr') {
          setDefinition(fullInd.ind_fr_exp);
          setTitle(
            <div>
              <div>{t('query.indicator_code')}: {data.ind_code}</div>
              <div>{fullInd?.ind_name_fr} [{fullUnit?.unit_name_fr || 'No unit'}]</div>
            </div>
          );
        }
        else { // ar
          setDefinition(fullInd.ind_arb_exp);
          setTitle(
            <div>
              <div>{t('query.indicator_code')}: {data.ind_code}</div>
              <div>{fullInd?.ind_name_arb} [{fullUnit?.unit_name_arb || 'No unit'}]</div>
            </div>
          );
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [record, indicatorsState, unitsState, language]);

  // —— NEW: translate the comma-separated categories string
  const localizedCategories = (() => {
    if (!indicatorData.categories) return "-";
    return indicatorData.categories
      .split(',')
      .map(rawEng => {
        const engTrim = rawEng.trim().toUpperCase();
        // find the matching cat object
        const catObj = Object.values(categoriesState.value)
          .find(c => c.cat_name_eng.toUpperCase() === engTrim);
        if (!catObj) return rawEng.trim();
        // pick name by language
        if (language === 'fr')  return catObj.cat_name_fr  || catObj.cat_name_eng;
        if (language === 'ar')  return catObj.cat_name_arb  || catObj.cat_name_eng;
        return catObj.cat_name_eng;
      })
      .join(', ');
  })();

const renderDefinition = (text) => {
  if (!text) return null;

  // Split by anchor tags and keep them
  const parts = [];
  const regex = /<a\s+href="([^"]+)"[^>]*>(.*?)<\/a>/gi;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // push preceding plain text
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const url = match[1];
    const label = match[2] || match[1];
    parts.push(
      <a key={url} href={url} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    );

    lastIndex = regex.lastIndex;
  }

  // push remaining text after last link
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};



  const formatDate = iso => {
    if (!iso) return '';
    const d = new Date(iso);
    const day   = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year  = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={onCancel}
      width="75%"
      footer={null}
      style={{
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
      }}
      bodyStyle={{
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
        fontFamily: 'Roboto, sans-serif',
        fontWeight: '300',
      }}
      className="indicator-modal"
    >
      {loading
        ? <Spin size="large" />
        : (
          <Descriptions column={1}>
            <Descriptions.Item label={t('ind_list.def')}>
              {renderDefinition(definition)}
            </Descriptions.Item>

            <Descriptions.Item label={t('ind_list.category')}>
              {localizedCategories}
            </Descriptions.Item>

            <Descriptions.Item label={t('ind_list.source')}>
              {indicatorData.source_name}
            </Descriptions.Item>

            <Descriptions.Item label={t('ind_list.data_period')}>
              {indicatorData.data_period}
            </Descriptions.Item>

            <Descriptions.Item label={t('ind_list.last_update_date')}>
            {formatDate(indicatorData.last_update_date)}
            </Descriptions.Item>
          </Descriptions>
        )
      }
    </Modal>
  );
};
